import {
  useGetAllWorkspacesQuery,
  useGetArchivedWorkspacesQuery,
  useGetWorkspacesQuery,
} from '@carboncopyco/data';
import { ArchiveIcon } from '@carboncopyco/icons';
import {
  PermissionAction,
  PermissionKey,
  type Workspace,
} from '@carboncopyco/types';
import { ColorHex, MultiDropdownFilter, TextBase } from '@carboncopyco/ui';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { WorkspaceAvatar } from '../../Workspaces';
import { usePermissionsContext } from '../../utils';
import { useDashboardProps } from '../DashboardContext';

type ListItem = {
  id: string;
  label: Workspace['workspaceName'];
  color: Workspace['color'];
  isDeleted: Workspace['isDeleted'];
};

export const checkboxClasses = (checked) =>
  classNames(
    'rounded-sm border outline-none active:outline-none active:ring-0 active:ring-offset-0 focus:outline-none focus:ring-0 focus:ring-offset-0',
    checked
      ? 'hover:text-ds-primary-400 text-ds-primary active:text-ds-primary-500 cursor-pointer'
      : 'cursor-pointer text-ds-primary border-ds-natural-200 hover:border-ds-primary-400 active:border-ds-primary-500'
  );

const renderItem = ({ checked, onChange, item }) => (
  <div className="px-2 h-9 cursor-pointer flex items-center w-full gap-2 focus:bg-ds-natural-50 focus:outline-none focus:rounded-md">
    <div className="flex gap-3 items-center w-full">
      <input
        checked={checked}
        onChange={onChange}
        type="checkbox"
        className={checkboxClasses(checked)}
      />
      <div className="gap-2 flex items-center grow">
        {item.isDeleted ? (
          <ArchiveIcon color={ColorHex.natural600} />
        ) : (
          <WorkspaceAvatar
            size="small"
            workspace={{
              workspaceName: item.label,
              color: item.color,
              workspaceType: 'public',
            }}
          />
        )}
        <div className="truncate max-w-[110px]">
          <TextBase
            color="text-ds-natural-600"
            label={item.label}
            ellipsis
          />
        </div>
      </div>
    </div>
  </div>
);

export const WorkspaceFilter = () => {
  const { organizationId } = useDashboardProps();
  const flags = useFlags();
  const { getPermissions } = usePermissionsContext();
  const expandWorkspacesPermission = getPermissions(
    PermissionKey.workspace,
    PermissionAction.getAllWorkspacesOfOrganization
  );
  const shouldGetExpandedWorkspaceList =
    flags.expandAdminRolePrivileges && expandWorkspacesPermission;
  const { workspaceIds, setWorkspaceIds, clearWorkspaceIds } =
    useDashboardProps();
  const { data: activeWorkspaces } = useGetWorkspacesQuery(
    { organizationId },
    { skip: shouldGetExpandedWorkspaceList }
  );
  const { data: archivedWorkspaces } = useGetArchivedWorkspacesQuery(
    { organizationId },
    { skip: shouldGetExpandedWorkspaceList }
  );
  const { data: allWorkspaces } = useGetAllWorkspacesQuery(
    { organizationId, removeDeleted: false },
    { skip: !shouldGetExpandedWorkspaceList }
  );

  const workspaces = shouldGetExpandedWorkspaceList
    ? allWorkspaces || []
    : [...(activeWorkspaces || []), ...(archivedWorkspaces || [])];

  const items =
    workspaces
      ?.map(
        ({ id, workspaceName, color, isDeleted }): ListItem => ({
          id: String(id),
          label: isDeleted ? `${workspaceName} (archived)` : workspaceName,
          color,
          isDeleted,
        })
      )
      .sort((a, b) => a.label.localeCompare(b.label)) ?? [];

  const selectedItems = workspaceIds
    ? Object.fromEntries(
        workspaceIds
          .split(',')
          .map((workspaceId) => [
            workspaceId,
            items.find((workspace) => workspace.id === workspaceId)?.label,
          ])
      )
    : {};

  return workspaces.length > 1 ? (
    <MultiDropdownFilter
      capsuleWidth={200}
      contentWidth={200}
      renderItem={renderItem}
      searchableGroup={{ items }}
      selectedItems={selectedItems}
      onChange={setWorkspaceIds}
      placeholder="All workspaces"
      onClear={clearWorkspaceIds}
      sideOffset={-10}
    />
  ) : null;
};
